import { useCallback, useContext, useMemo } from 'react';
import { DrinkUpServiceApiContext } from '../../DrinkUpServiceApiContext';
import { UserContext } from '../../hooks/useUser';
import { AdminRum, InventoryRun, InventoryRunItem } from '../../types';

interface UseInventoryApiReturn {
  fetchRums: () => Promise<AdminRum[]>;
  fetchInventoryRuns: () => Promise<InventoryRun[]>;
  fetchInventoryRunItems: () => Promise<InventoryRunItem[]>;
  createInventoryRun: () => Promise<InventoryRun>;
  updateInventoryRun: (run: InventoryRun) => Promise<InventoryRun>;
  createInventoryRunItem: (item: Omit<InventoryRunItem, 'id'>) => Promise<InventoryRunItem>;
  updateInventoryRunItem: (item: InventoryRunItem) => Promise<InventoryRunItem>;
  deleteInventoryRunItem: (itemId: string) => Promise<void>;
}

export function useInventoryApi(): UseInventoryApiReturn {
  const { authToken } = useContext(UserContext);
  const { drinkUpService } = useContext(DrinkUpServiceApiContext);

  const headers = useMemo(() => ({
    'Authorization': `Token ${authToken}`,
    'Content-Type': 'application/json',
  }), [authToken]);

  const fetchRums = useCallback(async (): Promise<AdminRum[]> => {
    const response = await drinkUpService.get<AdminRum[]>('/get_rums', { headers });
    return response.data;
  }, [drinkUpService, headers]);

  const fetchInventoryRuns = useCallback(async (): Promise<InventoryRun[]> => {
    const response = await drinkUpService.get<InventoryRun[]>('/inventory-runs/', { headers });
    return response.data;
  }, [drinkUpService, headers]);

  const fetchInventoryRunItems = useCallback(async (): Promise<InventoryRunItem[]> => {
    const response = await drinkUpService.get<InventoryRunItem[]>('/inventory-run-items/', { headers });
    return response.data;
  }, [drinkUpService, headers]);

  const createInventoryRun = useCallback(async (): Promise<InventoryRun> => {
    const response = await drinkUpService.post<InventoryRun>('/inventory-runs/', {
      start_date: new Date().toISOString(),
    }, { headers });
    return response.data;
  }, [drinkUpService, headers]);

  const updateInventoryRun = useCallback(async (run: InventoryRun): Promise<InventoryRun> => {
    const response = await drinkUpService.put<InventoryRun>(
      `/inventory-runs/${run.id}/`, run, { headers },
    );
    return response.data;
  }, [drinkUpService, headers]);

  const createInventoryRunItem = useCallback(async (
    item: Omit<InventoryRunItem, 'id'>,
  ): Promise<InventoryRunItem> => {
    const response = await drinkUpService.post<InventoryRunItem>(
      '/inventory-run-items/', item, { headers },
    );
    return response.data;
  }, [drinkUpService, headers]);

  const updateInventoryRunItem = useCallback(async (
    item: InventoryRunItem,
  ): Promise<InventoryRunItem> => {
    const response = await drinkUpService.put<InventoryRunItem>(
      `/inventory-run-items/${item.id}/`, item, { headers },
    );
    return response.data;
  }, [drinkUpService, headers]);

  const deleteInventoryRunItem = useCallback(async (itemId: string): Promise<void> => {
    await drinkUpService.delete(`/inventory-run-items/${itemId}/`, { headers });
  }, [drinkUpService, headers]);

  return useMemo(() => ({
    fetchRums,
    fetchInventoryRuns,
    fetchInventoryRunItems,
    createInventoryRun,
    updateInventoryRun,
    createInventoryRunItem,
    updateInventoryRunItem,
    deleteInventoryRunItem,
  }), [
    fetchRums, fetchInventoryRuns, fetchInventoryRunItems,
    createInventoryRun, updateInventoryRun, createInventoryRunItem,
    updateInventoryRunItem, deleteInventoryRunItem,
  ]);
}
